import { useState } from "react";
import { ChevronDown, ChevronUp, Download, Loader2, Mail, FileText, History } from "lucide-react";
import { toast } from "sonner";
import { API, COVERAGE_STATUS, LEDGER_STATUS, PREVIOUS_STATUS, Pill, SectionLabel, downloadBlob, blobErrorDetail } from "./reviewMeta";

function Counts({ items, statuses, testid }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {Object.entries(statuses).map(([k, meta]) => {
        const n = items.filter((c) => c.status === k).length;
        if (!n) return null;
        return (
          <span key={k} className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-2.5 py-1 ${meta.cls}`}>
            <span className="font-semibold tabular-nums">{n}</span> {meta.label}
          </span>
        );
      })}
    </div>
  );
}

function Refs({ idx }) {
  if (!idx || !idx.length) return null;
  return (
    <span className="text-[10px] font-mono text-stone-400 ml-1">
      {idx.map((i) => `#${i + 1}`).join(" ")}
    </span>
  );
}

/**
 * The review's proof of work: overall assessment, coverage checklist
 * against the proposal, correspondence ledger, comparison with the
 * previous review, and the internal summary download.
 */
export default function CoveragePanel({ job, readOnly = false }) {
  const [openLedger, setOpenLedger] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const coverage = job.coverage || [];
  const ledger = job.ledger || [];
  const prev = job.previous_comparison || [];
  const corr = job.sources?.correspondence || {};
  const verifier = job.verifier || {};

  const downloadSummary = async () => {
    setDownloading(true);
    try {
      await downloadBlob(`${API}/api/proposals/contract-review/${job.job_id}/summary`, `review-summary-${job.job_id}.docx`);
    } catch (err) {
      toast.error(await blobErrorDetail(err, "Could not build the summary."));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="contract-review-coverage">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <SectionLabel>What was checked</SectionLabel>
          <h2 className="text-xl text-slate-900">Coverage report</h2>
          <p className="text-xs text-stone-500 mt-1">
            Every material proposal item, every email commitment, and the fate of the previous review&apos;s findings.
            {verifier.anchors_cleared ? ` ${verifier.anchors_cleared} anchor(s) could not be located verbatim and will be appended.` : ""}
            {verifier.dropped?.length ? ` ${verifier.dropped.length} first pass finding(s) dropped by the verifier as duplicate or immaterial.` : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={downloadSummary}
          disabled={downloading}
          data-testid="contract-review-summary-download"
          className="inline-flex items-center gap-2 text-xs font-mono tracking-[0.1em] uppercase text-[#1B4F8A] border border-[#1B4F8A]/40 hover:bg-[#1B4F8A]/5 rounded-md px-3 py-2 transition-colors"
        >
          {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
          Internal summary (.docx)
        </button>
      </div>

      {job.overall && (
        <div className="rounded-xl border border-stone-200 bg-stone-50 p-4 mb-6" data-testid="contract-review-overall">
          <SectionLabel>Overall assessment</SectionLabel>
          <p className="text-sm text-slate-800 leading-relaxed">{job.overall}</p>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#1B4F8A]" />
            <p className="text-sm font-medium text-slate-900">Proposal coverage checklist</p>
          </div>
          <Counts items={coverage} statuses={COVERAGE_STATUS} testid="contract-review-coverage-counts" />
        </div>
        <div className="overflow-x-auto rounded-xl border border-stone-200">
          <table className="w-full text-xs">
            <thead className="bg-stone-50 text-[10px] font-mono uppercase tracking-[0.12em] text-stone-500">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Item</th>
                <th className="text-left px-3 py-2 font-medium w-32">Status</th>
                <th className="text-left px-3 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((c) => (
                <tr key={c.key} data-testid={`contract-review-coverage-row-${c.key}`} className="border-t border-stone-100 align-top">
                  <td className="px-3 py-2 text-slate-800">{c.label}</td>
                  <td className="px-3 py-2"><Pill meta={COVERAGE_STATUS[c.status] || COVERAGE_STATUS.not_checked} /></td>
                  <td className="px-3 py-2 text-slate-600 leading-relaxed">
                    {c.note}
                    <Refs idx={c.finding_indexes} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-2">
        <button
          type="button"
          onClick={() => setOpenLedger((v) => !v)}
          data-testid="contract-review-ledger-toggle"
          className="w-full flex items-center justify-between gap-3 mb-3"
        >
          <span className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#1B4F8A]" />
            <span className="text-sm font-medium text-slate-900">Correspondence ledger</span>
            <span className="text-xs text-stone-500">
              {corr.threads ? `${ledger.length} commitment(s) from ${corr.threads} thread(s)` : `none supplied${job.no_correspondence_reason ? `: ${job.no_correspondence_reason}` : ""}`}
            </span>
          </span>
          <span className="flex items-center gap-3">
            <Counts items={ledger} statuses={LEDGER_STATUS} testid="contract-review-ledger-counts" />
            {openLedger ? <ChevronUp className="w-4 h-4 text-stone-400" /> : <ChevronDown className="w-4 h-4 text-stone-400" />}
          </span>
        </button>
        {openLedger && ledger.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-stone-200" data-testid="contract-review-ledger">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 text-[10px] font-mono uppercase tracking-[0.12em] text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium w-28">Date</th>
                  <th className="text-left px-3 py-2 font-medium w-40">Who</th>
                  <th className="text-left px-3 py-2 font-medium">Commitment</th>
                  <th className="text-left px-3 py-2 font-medium w-32">In contract</th>
                </tr>
              </thead>
              <tbody>
                {ledger.map((e, i) => (
                  <tr key={i} data-testid={`contract-review-ledger-row-${i}`} className="border-t border-stone-100 align-top">
                    <td className="px-3 py-2 text-slate-600 whitespace-nowrap">{e.date || "unknown"}</td>
                    <td className="px-3 py-2 text-slate-800">
                      {e.who}
                      <span className="block text-[10px] font-mono uppercase text-stone-400">{e.side}</span>
                    </td>
                    <td className="px-3 py-2 text-slate-700 leading-relaxed">
                      {e.commitment}
                      <Refs idx={e.finding_indexes} />
                    </td>
                    <td className="px-3 py-2"><Pill meta={LEDGER_STATUS[e.status] || LEDGER_STATUS.unclear} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {prev.length > 0 && (
        <div className="mt-6" data-testid="contract-review-previous">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <History className="w-4 h-4 text-[#1B4F8A]" />
              <p className="text-sm font-medium text-slate-900">Changes since the previous review</p>
            </div>
            <Counts items={prev} statuses={PREVIOUS_STATUS} testid="contract-review-previous-counts" />
          </div>
          <div className="overflow-x-auto rounded-xl border border-stone-200">
            <table className="w-full text-xs">
              <thead className="bg-stone-50 text-[10px] font-mono uppercase tracking-[0.12em] text-stone-500">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Previous finding</th>
                  <th className="text-left px-3 py-2 font-medium w-36">In new draft</th>
                  <th className="text-left px-3 py-2 font-medium">Note</th>
                </tr>
              </thead>
              <tbody>
                {prev.map((p, i) => (
                  <tr key={i} data-testid={`contract-review-previous-row-${i}`} className="border-t border-stone-100 align-top">
                    <td className="px-3 py-2 text-slate-800">
                      <span className={`text-[10px] font-mono uppercase mr-1.5 ${p.severity === "red" ? "text-rose-600" : p.severity === "amber" ? "text-amber-600" : "text-emerald-600"}`}>{p.severity}</span>
                      {p.issue}
                      {p.kept_by_reviewer === false && <span className="block text-[10px] text-stone-400">excluded by reviewer last time</span>}
                    </td>
                    <td className="px-3 py-2"><Pill meta={PREVIOUS_STATUS[p.status] || PREVIOUS_STATUS.not_checked} /></td>
                    <td className="px-3 py-2 text-slate-600 leading-relaxed">{p.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {readOnly && <p className="sr-only">read only</p>}
    </div>
  );
}
